import ClickLogger from '@/components/common/ClickLogger';
import RequestAccordion from '@/components/templates/diagnosis/treatment-request/components/RequestAccordion';
import { RecentSymptomItem } from '@/entities/diagnosis';
import { EScheduleModeForLog, EWebTreatmentRequestLog } from '@/entities/diagnosis';
import { UserProfile } from '@/models/common';
import { Client } from '@/types/app';
import { cn } from '@/utils/style';
import { SymptomBottomSheet } from '@/widgets/symptom-selection';
import { Button } from 'doctornow-design-system';
import {
  AccordionActions,
  AccordionState,
  UseTelemedicineRequestViewModel,
} from '../hooks/useTelemedicineRequestViewModel';
import TelemedicineBookingDays from './TelemedicineBookingDays';
interface DiagnosisFormStepViewModelProps {
  bookingSchedulesInfo: UseTelemedicineRequestViewModel['bookingSchedulesInfo'];
  treatmentRequest: UseTelemedicineRequestViewModel['treatmentRequest'];
  accordionRefs: UseTelemedicineRequestViewModel['accordionRefs'];
  selectedDayScheduleLength: UseTelemedicineRequestViewModel['selectedDayScheduleLength'];
  selectedDay: UseTelemedicineRequestViewModel['selectedDay'];
  ssnInputRef: UseTelemedicineRequestViewModel['ssnInputRef'];
  isPreSignedUrlsLoading: UseTelemedicineRequestViewModel['isPreSignedUrlsLoading'];
  detailSymptomInputRef: UseTelemedicineRequestViewModel['detailSymptomInputRef'];
  recentlyDetailSymptomsCount: UseTelemedicineRequestViewModel['recentlyDetailSymptomsCount'];
  isChronicDiseaseCategory: UseTelemedicineRequestViewModel['isChronicDiseaseCategory'];
  chronicDiseaseCategoryWarningMessages: UseTelemedicineRequestViewModel['chronicDiseaseCategoryWarningMessages'];
  chronicDiseaseCategoryPlaceholder: UseTelemedicineRequestViewModel['chronicDiseaseCategoryPlaceholder'];
  isDoctorStepButtonDisabled: UseTelemedicineRequestViewModel['isDoctorStepButtonDisabled'];
  selectedTimeSlotTitle: UseTelemedicineRequestViewModel['selectedTimeSlotTitle'];
  selectBookingTargetDate: UseTelemedicineRequestViewModel['selectBookingTargetDate'];
  selectBookingTimeSlot: UseTelemedicineRequestViewModel['selectBookingTimeSlot'];
  toggleAsapRequest: UseTelemedicineRequestViewModel['toggleAsapRequest'];
  selectQuickMode: UseTelemedicineRequestViewModel['selectQuickMode'];
  selectGeneralMode: UseTelemedicineRequestViewModel['selectGeneralMode'];
  updatePatientSsn: UseTelemedicineRequestViewModel['updatePatientSsn'];
  resetPatientSsn: UseTelemedicineRequestViewModel['resetPatientSsn'];
  updateSymptom: UseTelemedicineRequestViewModel['updateSymptom'];
  onHandleChangeSymptomImage: UseTelemedicineRequestViewModel['onHandleChangeSymptomImage'];
  removeSymptomImage: UseTelemedicineRequestViewModel['removeSymptomImage'];
  openDetailSymptomsLoadBottomSheet: UseTelemedicineRequestViewModel['openDetailSymptomsLoadBottomSheet'];
  requestCameraPermission: UseTelemedicineRequestViewModel['requestCameraPermission'];
  handleDiagnosisStepNext: UseTelemedicineRequestViewModel['handleDiagnosisStepNext'];
}

interface Props extends DiagnosisFormStepViewModelProps {
  client: Client;
  categoryName: string;
  userProfile: UserProfile | null;
  diagnosisStepButtonText: string;
  isSymptomBottomSheetOpen: boolean;
  recentlyDetailSymptoms: RecentSymptomItem[];
  onCloseSymptomBottomSheet: () => void;
  onSymptomSelect: (selectedItem: RecentSymptomItem) => void;
}

export type { AccordionActions, AccordionState };

const DiagnosisFormStep = ({
  client,
  bookingSchedulesInfo,
  treatmentRequest,
  accordionRefs,
  selectedDayScheduleLength,
  selectedDay,
  ssnInputRef,
  categoryName,
  isPreSignedUrlsLoading,
  detailSymptomInputRef,
  recentlyDetailSymptomsCount,
  isChronicDiseaseCategory,
  chronicDiseaseCategoryWarningMessages,
  chronicDiseaseCategoryPlaceholder,
  isDoctorStepButtonDisabled,
  selectedTimeSlotTitle,
  userProfile,
  selectBookingTargetDate,
  selectBookingTimeSlot,
  toggleAsapRequest,
  selectQuickMode,
  selectGeneralMode,
  updatePatientSsn,
  resetPatientSsn,
  updateSymptom,
  onHandleChangeSymptomImage,
  removeSymptomImage,
  openDetailSymptomsLoadBottomSheet,
  requestCameraPermission,
  handleDiagnosisStepNext,
  diagnosisStepButtonText,
  isSymptomBottomSheetOpen,
  recentlyDetailSymptoms,
  onCloseSymptomBottomSheet,
  onSymptomSelect,
}: Props) => {
  return (
    <>
      <div className='min-h-[calc(100vh-160px)] pb-[140px]'>
        <RequestAccordion>
          <RequestAccordion.Header title={selectedTimeSlotTitle} iconName={'calendar_today'} />
          <RequestAccordion.ContentBody ref={accordionRefs.days} contentLength={selectedDayScheduleLength}>
            <TelemedicineBookingDays
              treatmentRequest={treatmentRequest}
              days={bookingSchedulesInfo.daySchedulesGroupedByTimePeriod}
              quickestMinutes={bookingSchedulesInfo.getQuickestAvailableMinutes()}
              selectBookingTargetDate={selectBookingTargetDate}
              selectBookingTimeSlot={selectBookingTimeSlot}
              toggleAsapRequest={toggleAsapRequest}
              selectQuickMode={selectQuickMode}
              selectGeneralMode={selectGeneralMode}
              selectedDay={selectedDay}
            />
          </RequestAccordion.ContentBody>
        </RequestAccordion>
        <RequestAccordion>
          <RequestAccordion.Header
            title={treatmentRequest.validateSsn() ? (userProfile?.name ?? '') : '주민등록번호 뒷자리'}
            iconName={'id_card'}
          />
          <RequestAccordion.ContentBody ref={accordionRefs.ssn}>
            <RequestAccordion.SSN
              client={client}
              ref={ssnInputRef}
              userProfile={userProfile}
              treatmentRequest={treatmentRequest}
              updatePatientSsn={updatePatientSsn}
              resetPatientSsn={resetPatientSsn}
            />
          </RequestAccordion.ContentBody>
        </RequestAccordion>
        <RequestAccordion hiddenBorder>
          <RequestAccordion.Header title={categoryName} iconName={'content_paste'} />
          <RequestAccordion.ContentBody ref={accordionRefs.detailSymptom}>
            <RequestAccordion.DetailSymptom
              client={client}
              isImageUploading={isPreSignedUrlsLoading}
              ref={detailSymptomInputRef}
              shouldShowChildInfo={false}
              treatmentRequest={treatmentRequest}
              updateSymptom={updateSymptom}
              onHandleChangeSymptomImage={onHandleChangeSymptomImage}
              onDeleteSymptomImage={removeSymptomImage}
              recentlyDetailSymptomsCount={recentlyDetailSymptomsCount}
              openDetailSymptomsLoadBottomSheet={openDetailSymptomsLoadBottomSheet}
              requestCameraPermission={requestCameraPermission}
              isChronicDiseaseCategory={isChronicDiseaseCategory}
              chronicDiseaseCategoryWarningMessages={chronicDiseaseCategoryWarningMessages}
              chronicDiseaseCategoryPlaceholder={chronicDiseaseCategoryPlaceholder}
            />
          </RequestAccordion.ContentBody>
        </RequestAccordion>
      </div>

      <div
        className={cn([
          'fixed right-0 bottom-0 left-0 z-50 mx-auto w-full max-w-[600px]',
          'desktop:absolute desktop:max-w-none',
        ])}
      >
        <div
          className={cn([
            'pointer-events-auto mx-auto flex w-full max-w-[600px] items-center justify-center bg-white px-[20px] pb-[16px]',
            'desktop:max-w-none',
          ])}
        >
          <ClickLogger
            params={{
              eventName: EWebTreatmentRequestLog.ClickNextFromDiagnosisStep,
              payload: {
                categoryId: treatmentRequest.form.categoryId ?? '',
                categoryName: categoryName,
                ...(treatmentRequest.hasScheduleModeSelected() && {
                  scheduleMode: treatmentRequest.isWebQuickScheduleRequest()
                    ? EScheduleModeForLog.Quick
                    : EScheduleModeForLog.General,
                }),
              },
            }}
          >
            <Button
              buttonColor={'primary'}
              size={'xl'}
              disabled={isDoctorStepButtonDisabled}
              onClick={handleDiagnosisStepNext}
              className={'w-full'}
            >
              {diagnosisStepButtonText}
            </Button>
          </ClickLogger>
        </div>
      </div>

      <SymptomBottomSheet
        open={isSymptomBottomSheetOpen}
        items={recentlyDetailSymptoms}
        onClose={onCloseSymptomBottomSheet}
        onSubmit={onSymptomSelect}
      />
    </>
  );
};

export default DiagnosisFormStep;
